import React, { useEffect, useState } from "react";
import axios from "axios";
import Constants from "Helpers/Constants";
import init from "Helpers/WindowToken";

import {
  Card,
  Grid,
  TextField,
  IconButton,
  Tooltip,
} from "@mui/material";

import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";

import DataTable from "examples/Tables/DataTable";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import MDSnackbar from "components/MDSnackbar";

const TrustedBrands = () => {
  const [brands, setBrands] = useState([]);
  const [brandName, setBrandName] = useState("");
  const [brandLogo, setBrandLogo] = useState(null);
  const [link, setLink] = useState("");

  const [editingId, setEditingId] = useState(null);

  const [loading, setLoading] = useState(false);

  const [notification, setNotification] = useState(false);
  const [message, setMessage] = useState("");
  const [notiColor, setNotiColor] = useState("success");

  /* ==========================
     FETCH BRANDS
  ========================== */
  const getBrands = async () => {
    if (init() !== "success") return;

    try {
      const res = await axios.get(Constants.getUrls.getTrustedBrands);
      setBrands(res.data.data.docs || []);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    getBrands();
  }, []);

  /* ==========================
     ADD BRAND
  ========================== */
  const handleAddBrand = async (e) => {
    e.preventDefault();

    if (!brandName || !brandLogo) {
      setNotification(true);
      setNotiColor("warning");
      setMessage("Brand name and logo are required");
      return;
    }

    const fd = new FormData();
    fd.append("brand_name", brandName);
    fd.append("brand_logo", brandLogo);
    fd.append("link", link);

    try {
      setLoading(true);
      await axios.post(Constants.postUrls.createTrustedBrand, fd);

      setNotification(true);
      setNotiColor("success");
      setMessage("Brand added successfully");

      resetForm();
      getBrands();
    } catch (err) {
      setNotification(true);
      setNotiColor("error");
      setMessage("Failed to add brand");
    } finally {
      setLoading(false);
    }
  };

  /* ==========================
     UPDATE BRAND
  ========================== */
  const handleUpdateBrand = async (e) => {
    e.preventDefault();

    const fd = new FormData();
    fd.append("brand_name", brandName);
    fd.append("link", link);

    if (brandLogo) {
      fd.append("brand_logo", brandLogo);
    }

    try {
      setLoading(true);

      await axios.put(
        Constants.putUrls.updateTrustedBrand + editingId,
        fd
      );

      setNotification(true);
      setNotiColor("success");
      setMessage("Brand updated successfully");

      resetForm();
      getBrands();
    } catch (err) {
      setNotification(true);
      setNotiColor("error");
      setMessage("Update failed");
    } finally {
      setLoading(false);
    }
  };

  /* ==========================
     EDIT CLICK
  ========================== */
  const handleEdit = (brand) => {
    setEditingId(brand._id);
    setBrandName(brand.brand_name);
    setLink(brand.link || "");
    setBrandLogo(null);
  };

  /* ==========================
     RESET FORM
  ========================== */
  const resetForm = () => {
    setBrandName("");
    setBrandLogo(null);
    setLink("");
    setEditingId(null);
  };

  /* ==========================
     DELETE BRAND
  ========================== */
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this brand?")) return;

    try {
      await axios.delete(Constants.deleteUrls.deleteTrustedBrand + id);

      setNotification(true);
      setNotiColor("success");
      setMessage("Brand deleted");

      getBrands();
    } catch (err) {
      setNotification(true);
      setNotiColor("error");
      setMessage("Delete failed");
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox pt={6} pb={3}>
        <Card>
          <MDBox
            mx={2}
            mt={-3}
            py={3}
            px={2}
            variant="gradient"
            bgColor="info"
            borderRadius="lg"
            coloredShadow="info"
          >
            <MDTypography variant="h6" color="white">
              Trusted Brands
            </MDTypography>
          </MDBox>

          {/* ================= FORM ================= */}
          <MDBox p={3}>
            <form onSubmit={editingId ? handleUpdateBrand : handleAddBrand}>
              <Grid container spacing={2}>
                <Grid item sm={4}>
                  <TextField
                    label="Brand Name"
                    fullWidth
                    value={brandName}
                    onChange={(e) => setBrandName(e.target.value)}
                  />
                </Grid>

                <Grid item sm={4}>
                  <TextField
                    type="file"
                    fullWidth
                    focused
                    onChange={(e) => setBrandLogo(e.target.files[0])}
                  />
                </Grid>

                <Grid item sm={4}>
                  <TextField
                    type="text"
                    value={link}
                    onChange={(e) => setLink(e.target.value)}
                    fullWidth
                    label="Brand Link"
                  />
                </Grid>

                <Grid item sm={4} alignSelf="center">
                  <MDButton
                    variant="contained"
                    color={editingId ? "warning" : "info"}
                    type="submit"
                    disabled={loading}
                  >
                    {loading
                      ? editingId
                        ? "Updating..."
                        : "Adding..."
                      : editingId
                      ? "Update Brand"
                      : "Add Brand"}
                  </MDButton>

                  {editingId && (
                    <MDButton
                      sx={{ ml: 2 }}
                      variant="outlined"
                      color="secondary"
                      onClick={resetForm}
                    >
                      Cancel
                    </MDButton>
                  )}
                </Grid>
              </Grid>
            </form>
          </MDBox>

          {/* ================= TABLE ================= */}
          <MDBox px={3} pb={3}>
            <DataTable
              table={{
                columns: [
                  { Header: "S.No", accessor: "serial", width: "10%" },
                  { Header: "Brand Name", accessor: "brand_name" },
                  { Header: "Logo", accessor: "brand_logo" },
                  { Header: "Link", accessor: "link" },
                  { Header: "Actions", accessor: "action" },
                ],
                rows: brands.map((brand, index) => ({
                  serial: index + 1,
                  brand_name: brand.brand_name,
                  brand_logo: (
                    <img
                      src={Constants.imageDomain + brand.brand_logo}
                      alt={brand.brand_name}
                      style={{ height: 40, objectFit: "contain" }}
                    />
                  ),
                  link: brand?.link,
                  action: (
                    <>
                      <Tooltip title="Edit Brand">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleEdit(brand)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Delete Brand">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete(brand._id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  ),
                })),
              }}
              entriesPerPage={false}
              showTotalEntries={false}
              isSorted={false}
            />
          </MDBox>

          <MDSnackbar
            color={notiColor}
            icon="notifications"
            title="Trusted Brands"
            content={message}
            dateTime="Just now"
            open={notification}
            onClose={() => setNotification(false)}
          />
        </Card>
      </MDBox>
    </DashboardLayout>
  );
};

export default TrustedBrands;

// import React, { useEffect, useState } from "react";
// import axios from "axios";
// import Constants from "Helpers/Constants";
// import init from "Helpers/WindowToken";

// import {
//   Card,
//   Grid,
//   TextField,
//   IconButton,
//   Tooltip,
// } from "@mui/material";

// import DeleteIcon from "@mui/icons-material/Delete";

// import DataTable from "examples/Tables/DataTable";
// import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
// import DashboardNavbar from "examples/Navbars/DashboardNavbar";

// import MDBox from "components/MDBox";
// import MDButton from "components/MDButton";
// import MDTypography from "components/MDTypography";
// import MDSnackbar from "components/MDSnackbar";

// const TrustedBrands = () => {
//   const [brands, setBrands] = useState([]);
//   const [brandName, setBrandName] = useState("");
//   const [brandLogo, setBrandLogo] = useState(null);
//   const [loading, setLoading] = useState(false);
//   const [link, setLink] = useState("")

//   const [notification, setNotification] = useState(false);
//   const [message, setMessage] = useState("");
//   const [notiColor, setNotiColor] = useState("success");

//   /* ==========================
//      FETCH BRANDS
//   ========================== */
//   const getBrands = async () => {
//     if (init() !== "success") return;

//     try {
//       const res = await axios.get(Constants.getUrls.getTrustedBrands);
//       setBrands(res.data.data.docs || []);
//     } catch (err) {
//       console.error(err);
//     }
//   };

//   useEffect(() => {
//     getBrands();
//   }, []);

//   /* ==========================
//      ADD BRAND
//   ========================== */
//   const handleAddBrand = async (e) => {
//     e.preventDefault();

//     if (!brandName || !brandLogo) {
//       setNotification(true);
//       setNotiColor("warning");
//       setMessage("Brand name and logo are required");
//       return;
//     }

//     const fd = new FormData();
//     fd.append("brand_name", brandName);
//     fd.append("brand_logo", brandLogo);
//     fd.append("link", link)

//     try {
//       setLoading(true);
//       await axios.post(Constants.postUrls.createTrustedBrand, fd);

//       setNotification(true);
//       setNotiColor("success");
//       setMessage("Brand added successfully");

//       setBrandName("");
//       setBrandLogo(null);

//       getBrands();
//     } catch (err) {
//       setNotification(true);
//       setNotiColor("error");
//       setMessage("Failed to add brand");
//     } finally {
//       setLoading(false);
//     }
//   };

//   /* ==========================
//      DELETE BRAND
//   ========================== */
//   const handleDelete = async (id) => {
//     if (!window.confirm("Delete this brand?")) return;

//     try {
//       await axios.delete(Constants.deleteUrls.deleteTrustedBrand + id);

//       setNotification(true);
//       setNotiColor("success");
//       setMessage("Brand deleted");

//       getBrands();
//     } catch (err) {
//       setNotification(true);
//       setNotiColor("error");
//       setMessage("Delete failed");
//     }
//   };

//   return (
//     <DashboardLayout>
//       <DashboardNavbar />

//       <MDBox pt={6} pb={3}>
//         <Card>
//           <MDBox
//             mx={2}
//             mt={-3}
//             py={3}
//             px={2}
//             variant="gradient"
//             bgColor="info"
//             borderRadius="lg"
//             coloredShadow="info"
//           >
//             <MDTypography variant="h6" color="white">
//               Trusted Brands
//             </MDTypography>
//           </MDBox>

//           {/* ================= ADD BRAND ================= */}
//           <MDBox p={3}>
//             <form onSubmit={handleAddBrand}>
//               <Grid container spacing={2}>
//                 <Grid item sm={4}>
//                   <TextField
//                     label="Brand Name"
//                     fullWidth
//                     value={brandName}
//                     onChange={(e) => setBrandName(e.target.value)}
//                   />
//                 </Grid>

//                 <Grid item sm={4}>
//                   <TextField
//                     type="file"
//                     fullWidth
//                     focused
//                     onChange={(e) => setBrandLogo(e.target.files[0])}
//                   />
//                 </Grid>

//                 <Grid item sm={4}>
//                   <TextField
//                     type="text"
//                     onChange={(e)=> setLink(e.target.value)}
//                     fullWidth
//                     focused
//                     // onChange={(e) => setBrandLogo(e.target.files[0])}
//                   />
//                 </Grid>

//                 <Grid item sm={4} alignSelf="center">
//                   <MDButton
//                     variant="contained"
//                     color="info"
//                     type="submit"
//                     disabled={loading}
//                   >
//                     {loading ? "Adding..." : "Add Brand"}
//                   </MDButton>
//                 </Grid>
//               </Grid>
//             </form>
//           </MDBox>

//           {/* ================= LIST BRANDS (DATATABLE) ================= */}
//           <MDBox px={3} pb={3}>
//             <DataTable
//               table={{
//                 columns: [
//                   { Header: "S.No", accessor: "serial", width: "10%" },
//                   { Header: "Brand Name", accessor: "brand_name" },
//                   { Header: "Logo", accessor: "brand_logo" },
//                   { Header: "Link", accessor: "link"},
//                   { Header: "Actions", accessor: "action" },
//                 ],
//                 rows: brands.map((brand, index) => ({
//                   serial: index + 1,
//                   brand_name: brand.brand_name,
//                   brand_logo: (
//                     <img
//                       src={Constants.imageDomain + brand.brand_logo}
//                       alt={brand.brand_name}
//                       style={{ height: 40, objectFit: "contain" }}
//                     />
//                   ),
//                   link: brand?.link,
//                   action: (
//                     <Tooltip title="Delete Brand">
//                       <IconButton
//                         size="small"
//                         color="error"
//                         onClick={() => handleDelete(brand._id)}
//                       >
//                         <DeleteIcon fontSize="small" />
//                       </IconButton>
//                     </Tooltip>
//                   ),
//                 })),
//               }}
//               entriesPerPage={false}
//               showTotalEntries={false}
//               isSorted={false}
//             />
//           </MDBox>

//           <MDSnackbar
//             color={notiColor}
//             icon="notifications"
//             title="Trusted Brands"
//             content={message}
//             dateTime="Just now"
//             open={notification}
//             onClose={() => setNotification(false)}
//           />
//         </Card>
//       </MDBox>
//     </DashboardLayout>
//   );
// };

// export default TrustedBrands;
